import type { Event } from '@sentry/react'

// WebKit's IndexedDB connection breaking under Firestore surfaces as many
// different messages across releases; firestoreRecovery.ts restarts the WebView
// when it happens. One issue keeps the rate visible without flooding the list.
// firestoreRecovery.ts also restarts on this signal when auth is still unresolved.
const INDEXEDDB_BROKEN =
  /without an in-progress transaction|cursor that doesn't exist|Database deleted by request of the user|Unable to open database file|on 'IDBDatabase'|IndexedDbTransactionError|exclusive access to the persistence layer|Pending promise was never set|INTERNAL ASSERTION FAILED/

let brokenThisSession = false
const listeners: Array<(detail: string) => void> = []

export function isIndexedDbBreakage(event: Event, brokenEarlier = brokenThisSession): boolean {
  const values = event.exception?.values ?? []
  if (values.some(v => INDEXEDDB_BROKEN.test(v.value ?? ''))) return true
  // Sometimes the breakage throws a bare AbortError and the telling message
  // only reaches the console before it — possibly well before, so any earlier
  // breakage this session counts.
  if (!isAbortError(event)) return false
  // An AbortError while Firestore's own database open never completed is the
  // same breakage in another shape: the open hung, and the abort is it (or a
  // transaction queued on it) being torn down (#611). The tag comes from
  // sessionDiagnostics' event processor, which runs before beforeSend.
  if (String(event.tags?.idb_pending ?? '').includes('firestore/')) return true
  return brokenEarlier || (event.breadcrumbs ?? []).some(b => INDEXEDDB_BROKEN.test(b.message ?? ''))
}

export function isAbortError(event: Event): boolean {
  return (event.exception?.values ?? []).some(v => /AbortError/.test(`${v.type ?? ''} ${v.value ?? ''}`))
}

// WebKit closes a backgrounded page's IndexedDB connections; a Firebase side
// database (Installations, Auth persistence, heartbeat) then throws "connection
// is closing" from Performance's config fetch, which falls back to defaults.
// With Firestore's own connection intact the page is fine (GlitchTip 8497705's
// follow-up: auth resolved, first snapshot, join completed), so it's not a
// breakage. The idb_closed tag comes from sessionDiagnostics' event processor.
export function isSideDatabaseClosing(event: Event): boolean {
  const values = event.exception?.values ?? []
  if (!values.some(v => /connection is closing/.test(v.value ?? ''))) return false
  const closed = String(event.tags?.idb_closed ?? '').split(',').filter(Boolean)
  return closed.length > 0 && !closed.some(name => name.startsWith('firestore/'))
}

export function noteIndexedDbBreakage(detail: string) {
  if (brokenThisSession) return
  brokenThisSession = true
  for (const cb of listeners) {
    try {
      cb(detail)
    } catch {
      // A failing listener must not stop the others or the Sentry event.
    }
  }
}

export function onIndexedDbBreakage(cb: (detail: string) => void) {
  listeners.push(cb)
  if (brokenThisSession) cb('earlier')
}
